import {
	motion,
	useReducedMotion,
	useMotionValue,
	useSpring,
} from 'framer-motion';
import { useEffect, useState } from 'react';

interface Particle {
	id: number;
	x: number;
	y: number;
	baseX: number;
	baseY: number;
	vx: number;
	vy: number;
}

export default function Hero() {
	const reduce = useReducedMotion();
	const mouseX = useMotionValue(50);
	const mouseY = useMotionValue(50);
	const smoothMouseX = useSpring(mouseX, { stiffness: 30, damping: 15 });
	const smoothMouseY = useSpring(mouseY, { stiffness: 30, damping: 15 });

	const [particles, setParticles] = useState<Particle[]>([]);
	const [currentMouseX, setCurrentMouseX] = useState(50);
	const [currentMouseY, setCurrentMouseY] = useState(50);

	// Initialize particles
	useEffect(() => {
		const particleCount = 1440;
		const newParticles: Particle[] = [];

		for (let i = 0; i < particleCount; i++) {
			const x = Math.random() * 100;
			const y = Math.random() * 100;
			newParticles.push({
				id: i,
				x,
				y,
				baseX: x,
				baseY: y,
				vx: (Math.random() - 0.5) * 0.01,
				vy: (Math.random() - 0.5) * 0.01,
			});
		}

		setParticles(newParticles);
	}, []);

	// Track mouse and update particle positions
	useEffect(() => {
		if (reduce) return;

		const handleMouseMove = (e: MouseEvent) => {
			const x = (e.clientX / window.innerWidth) * 100;
			const y = (e.clientY / window.innerHeight) * 100;
			mouseX.set(x);
			mouseY.set(y);
			setCurrentMouseX(x);
			setCurrentMouseY(y);
		};

		window.addEventListener('mousemove', handleMouseMove);
		return () => window.removeEventListener('mousemove', handleMouseMove);
	}, [mouseX, mouseY, reduce]);

	// Animate particles with mouse attraction
	useEffect(() => {
		if (reduce || particles.length === 0) return;

		const interval = setInterval(() => {
			setParticles((prev) =>
				prev.map((particle) => {
					// Calculate distance to mouse
					const dx = currentMouseX - particle.x;
					const dy = currentMouseY - particle.y;
					const distance = Math.sqrt(dx * dx + dy * dy);

					// Mouse attraction force
					const attractionRadius = 25;
					const attractionStrength = 0.15;
					let forceX = 0;
					let forceY = 0;

					if (distance < attractionRadius && distance > 0) {
						const force =
							(1 - distance / attractionRadius) * attractionStrength;
						forceX = (dx / distance) * force;
						forceY = (dy / distance) * force;
					}

					// Gentle drift back to base position
					const returnForce = 0.02;
					const baseForceX = (particle.baseX - particle.x) * returnForce;
					const baseForceY = (particle.baseY - particle.y) * returnForce;

					// Update position with drift + mouse attraction + return force
					let newX = particle.x + particle.vx + forceX + baseForceX;
					let newY = particle.y + particle.vy + forceY + baseForceY;
					let newVx = particle.vx * 0.95; // Damping
					let newVy = particle.vy * 0.95;

					// Keep within bounds
					newX = Math.max(0, Math.min(100, newX));
					newY = Math.max(0, Math.min(100, newY));

					return {
						...particle,
						x: newX,
						y: newY,
						vx: newVx,
						vy: newVy,
					};
				})
			);
		}, 30);

		return () => clearInterval(interval);
	}, [particles.length, currentMouseX, currentMouseY, reduce]);

	return (
		<section className='relative isolate min-h-[92vh] grid place-items-center overflow-hidden bg-slate-950'>
			{/* Sophisticated gradient background */}
			<div className='absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 opacity-90' />
			<div className='absolute inset-0 bg-gradient-to-t from-blue-950/30 via-transparent to-transparent' />
			<motion.div
				aria-hidden
				className='absolute inset-0 opacity-25 mix-blend-screen'
				style={{
					backgroundImage:
						'radial-gradient(circle at 20% 20%, rgba(59,130,246,0.18) 0, transparent 45%), radial-gradient(circle at 80% 30%, rgba(14,116,144,0.15) 0, transparent 40%), radial-gradient(circle at 50% 80%, rgba(56,189,248,0.12) 0, transparent 45%)',
					backgroundSize: '140% 140%',
				}}
				animate={
					reduce
						? false
						: {
								backgroundPosition: [
									'0% 0%',
									'40% 60%',
									'80% 30%',
									'50% 80%',
									'0% 0%',
								],
						  }
				}
				transition={
					reduce
						? undefined
						: {
								duration: 24,
								repeat: Infinity,
								ease: 'easeInOut',
						  }
				}
			/>

			{/* Binary Particle Animation */}
			<svg
				className='absolute inset-0 w-full h-full'
				viewBox='0 0 100 100'
				preserveAspectRatio='xMidYMid slice'>
				<defs>
					<linearGradient
						id='particle-gradient'
						x1='0%'
						y1='0%'
						x2='100%'
						y2='100%'>
						<stop offset='0%' stopColor='#bfdbfe' />
						<stop offset='100%' stopColor='#60a5fa' />
					</linearGradient>
					<radialGradient id='mouse-glow'>
						<stop offset='0%' stopColor='#60A5FA' stopOpacity='0.4' />
						<stop offset='50%' stopColor='#3B82F6' stopOpacity='0.2' />
						<stop offset='100%' stopColor='#1E40AF' stopOpacity='0' />
					</radialGradient>
				</defs>

				{/* Mouse glow effect */}
				<motion.circle
					cx={currentMouseX}
					cy={currentMouseY}
					r={25}
					fill='url(#mouse-glow)'
					initial={{ opacity: 0 }}
					animate={{ opacity: 0.6 }}
					transition={{ duration: 0.3 }}
				/>

				{/* Binary glyphs */}
				{particles.map((particle) => {
					const distToMouse = Math.sqrt(
						Math.pow(particle.x - currentMouseX, 2) +
							Math.pow(particle.y - currentMouseY, 2)
					);
					const isNearMouse = distToMouse < 9;
					const char = isNearMouse ? '1' : '0';
					const fill = isNearMouse
						? 'rgba(191,219,254,0.92)'
						: 'rgba(148,163,184,0.42)';
					const fontSize = isNearMouse ? 1.1 : 1.08;
					const opacity = isNearMouse ? 0.8 : 0.32;

					return (
						<motion.text
							key={`particle-${particle.id}`}
							x={particle.x}
							y={particle.y}
							textAnchor='middle'
							dominantBaseline='middle'
							fontFamily='var(--font-mono, "IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace)'
							fontSize={fontSize}
							fontWeight={600}
							fill={fill}
							initial={false}
							animate={{ fill, opacity }}
							style={{ letterSpacing: '-0.04em' }}
							transition={{ duration: 0.25 }}>
							{char}
						</motion.text>
					);
				})}
			</svg>

			{/* Background glyph (Devanagari: अस्माकम्) */}
			<div
				aria-hidden
				className='pointer-events-none absolute inset-0 grid place-items-center'>
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 0.02 }}
					transition={{ duration: 1.2, delay: 0.3 }}
					className='font-display text-[18vw] leading-none text-blue-400 select-none'>
					अस्माकम्
				</motion.div>
			</div>

			{/* Content */}
			<div className='relative z-10 mx-auto max-w-6xl px-6 py-24 text-center'>
				<motion.h1
					initial={{ y: 20, opacity: 0 }}
					animate={{ y: 0, opacity: 1 }}
					transition={{ duration: 0.7 }}
					className='font-display text-4xl sm:text-6xl md:text-7xl font-semibold tracking-tight text-white'>
					<span className='block'>Actualizing Ideas.</span>
					<span className='block'>0 → 1.</span>
				</motion.h1>
				<motion.p
					initial={{ y: 20, opacity: 0 }}
					animate={{ y: 0, opacity: 1 }}
					transition={{ duration: 0.7, delay: 0.2 }}
					className='mt-6 text-lg sm:text-xl text-blue-100/80 max-w-2xl mx-auto'>
					Converting AI concepts into production software and operating SaaS
					that customers actually use.
				</motion.p>
				<motion.div
					initial={{ y: 20, opacity: 0 }}
					animate={{ y: 0, opacity: 1 }}
					transition={{ duration: 0.7, delay: 0.4 }}
					className='mt-8 flex gap-4 justify-center'>
					<a
						href='/sms'
						className='rounded-2xl bg-blue-600 text-white px-6 py-3 font-medium hover:bg-blue-500 transition-colors focus-ring'>
						Join Us - Get SMS Updates
					</a>
					<a
						href='#work'
						className='rounded-2xl border border-blue-400/30 text-blue-100 px-6 py-3 font-medium hover:bg-blue-900/30 transition-colors focus-ring'>
						View Products
					</a>
				</motion.div>
			</div>
		</section>
	);
}
